import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";

import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReplayIcon from "@mui/icons-material/Replay";

import FactoryStockService from "../../services/FactoryStockService";
// Change the above import path according to your project structure.


const BulkFotaQueue = () => {
    /* =========================================================
       SECTION 1 - TO DO LIST
    ========================================================= */

    const [todoDevices, setTodoDevices] = useState([]);
    const [todoLoading, setTodoLoading] = useState(false);

const [searchInput, setSearchInput] = useState("");
const [search, setSearch] = useState("");
    // Selected IMEIs from To-do table
    const [selectedImeis, setSelectedImeis] = useState([]);


    /* =========================================================
       SECTION 2 - MANUFACTURER / MODEL / FIRMWARE
    ========================================================= */

    const [vendor, setVendor] = useState("");

    const [manufacturers, setManufacturers] = useState([]);

    const [selectedManufacturer, setSelectedManufacturer] = useState("");

    const [selectedModel, setSelectedModel] = useState("");

    const [selectedFirmware, setSelectedFirmware] = useState("");

    const [catalogLoading, setCatalogLoading] = useState(false);


    /* =========================================================
       SECTION 4 - QUEUE STATUS
    ========================================================= */

    const [queueRows, setQueueRows] = useState([]);

    const [queueLoading, setQueueLoading] = useState(false);

    // IMEIs selected from Queue Status table for Mark Config Done
    const [selectedReadyImeis, setSelectedReadyImeis] = useState([]);


    /* =========================================================
       GENERAL
    ========================================================= */

    const [startQueueLoading, setStartQueueLoading] = useState(false);

    const [markDoneLoading, setMarkDoneLoading] = useState(false);

    const [retryLoading, setRetryLoading] = useState("");

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });


    /* =========================================================
       SNACKBAR
    ========================================================= */

    const showMessage = (message, severity = "success") => {
        setSnackbar({
            open: true,
            message,
            severity,
        });
    };

    const handleSnackbarClose = () => {
        setSnackbar((prev) => ({
            ...prev,
            open: false,
        }));
    };


    /* =========================================================
       API - TODO DEVICE LIST
    ========================================================= */

    const loadTodoDevices = useCallback(async () => {
        try {
            setTodoLoading(true);

            const response = await FactoryStockService.configList({
                page: 1,
                page_size: 100,
                search: search.trim(),
            });

            const data = response?.data;

            setTodoDevices(data?.results || []);

            // Remove selections which no longer exist
            const availableImeis = new Set(
                (data?.results || []).map((item) => String(item.imei))
            );

            setSelectedImeis((previous) =>
                previous.filter((imei) => availableImeis.has(String(imei)))
            );
        } catch (error) {
            console.error("Error loading config devices:", error);

            showMessage(
                error?.response?.data?.detail ||
                    error?.response?.data?.message ||
                    "Failed to load device list",
                "error"
            );
        } finally {
            setTodoLoading(false);
        }
    }, [search]);

const handleSearch = () => {
    setSearch(searchInput.trim());
};

const handleRefresh = () => {
    setSearchInput("");
    setSearch("");
};

    /* =========================================================
       API - QUEUE STATUS
    ========================================================= */

    const loadQueueStatus = useCallback(async () => {
        try {
            setQueueLoading(true);

            const response =
                await FactoryStockService.fotaQueueStatus();

            const data = response?.data;

            setQueueRows(data?.results || []);
        } catch (error) {
            console.error("Error loading queue status:", error);

            showMessage(
                error?.response?.data?.detail ||
                    error?.response?.data?.message ||
                    "Failed to load queue status",
                "error"
            );
        } finally {
            setQueueLoading(false);
        }
    }, []);


    /* =========================================================
       INITIAL API CALLS
    ========================================================= */

    useEffect(() => {
        loadTodoDevices();
        loadQueueStatus();
    }, [loadTodoDevices, loadQueueStatus]);


    /* =========================================================
       VENDOR CHANGE
    ========================================================= */

    const handleVendorChange = async (event) => {
        const value = event.target.value;

        setVendor(value);

        // Reset dependent dropdowns
        setManufacturers([]);
        setSelectedManufacturer("");
        setSelectedModel("");
        setSelectedFirmware("");

        if (!value) {
            return;
        }

        try {
            setCatalogLoading(true);

            const response =
                await FactoryStockService.getVendorManufacturerCatalog(
                    value
                );

            const data = response?.data;

            if (Array.isArray(data)) {
                setManufacturers(data);
            } else {
                setManufacturers([]);
            }
        } catch (error) {
            console.error(
                "Error loading manufacturer catalog:",
                error
            );

            showMessage(
                error?.response?.data?.detail ||
                    error?.response?.data?.message ||
                    "Failed to load manufacturer catalog",
                "error"
            );
        } finally {
            setCatalogLoading(false);
        }
    };


    /* =========================================================
       MANUFACTURER / MODEL / FIRMWARE OPTIONS
    ========================================================= */

    const manufacturerOptions = manufacturers || [];

    const selectedManufacturerObject = useMemo(() => {
        return manufacturerOptions.find(
            (manufacturer) =>
                String(manufacturer.id) ===
                String(selectedManufacturer)
        );
    }, [manufacturerOptions, selectedManufacturer]);


    const modelOptions =
        selectedManufacturerObject?.device_models || [];


    const selectedModelObject = useMemo(() => {
        return modelOptions.find(
            (model) =>
                String(model.id) === String(selectedModel)
        );
    }, [modelOptions, selectedModel]);


    const firmwareOptions =
        selectedModelObject?.firmwares || [];


    /* =========================================================
       TODO TABLE SELECTION
    ========================================================= */

    const visibleTodoImeis = useMemo(() => {
        return todoDevices
            .map((device) => String(device.imei))
            .filter(Boolean);
    }, [todoDevices]);


    const allTodoSelected =
        visibleTodoImeis.length > 0 &&
        visibleTodoImeis.every((imei) =>
            selectedImeis.includes(imei)
        );


    const handleTodoRowSelection = (imei) => {
        const imeiString = String(imei);

        setSelectedImeis((previous) => {
            if (previous.includes(imeiString)) {
                return previous.filter(
                    (item) => item !== imeiString
                );
            }

            return [...previous, imeiString];
        });
    };


    const handleSelectAllTodo = (event) => {
        if (event.target.checked) {
            setSelectedImeis(visibleTodoImeis);
        } else {
            setSelectedImeis([]);
        }
    };


    /* =========================================================
       START QUEUE
    ========================================================= */

    const handleStartQueue = async () => {
        if (selectedImeis.length === 0) {
            showMessage(
                "Please select at least one device.",
                "warning"
            );
            return;
        }

        if (!vendor) {
            showMessage(
                "Please select vendor.",
                "warning"
            );
            return;
        }

        if (!selectedManufacturer) {
            showMessage(
                "Please select manufacturer.",
                "warning"
            );
            return;
        }

        if (!selectedModel) {
            showMessage(
                "Please select model.",
                "warning"
            );
            return;
        }

        if (!selectedFirmware) {
            showMessage(
                "Please select firmware.",
                "warning"
            );
            return;
        }

        const manufacturer =
            selectedManufacturerObject;

        const model = selectedModelObject;

        const firmware =
            firmwareOptions.find(
                (item) =>
                    String(item.id) ===
                    String(selectedFirmware)
            );

        if (!manufacturer || !model || !firmware) {
            showMessage(
                "Invalid manufacturer/model/firmware selection.",
                "error"
            );
            return;
        }

        /*
         * API requires:
         *
         * imeis
         * vendor
         * model_id
         * protocol_format
         * manufacturer_id
         */

        const payload = {
            imeis: selectedImeis,
            vendor: vendor,
            model_id: model.id,
            protocol_format:
                firmware.fw_name,
            manufacturer_id:
                manufacturer.id,
        };

        try {
            setStartQueueLoading(true);

            const response =
                await FactoryStockService.fotaQueueAdd(
                    payload
                );

            const data = response?.data;

            const createdCount =
                data?.created?.length || 0;

            const skippedCount =
                data?.skipped?.length || 0;

            if (createdCount > 0) {
                showMessage(
                    `${createdCount} device(s) added to FOTA queue${
                        skippedCount > 0
                            ? `, ${skippedCount} skipped`
                            : ""
                    }.`,
                    skippedCount > 0
                        ? "warning"
                        : "success"
                );
            } else {
                showMessage(
                    "No devices were added to the queue.",
                    "warning"
                );
            }

            // Refresh both tables
            await loadTodoDevices();
            await loadQueueStatus();

            // Clear selected devices
            setSelectedImeis([]);
        } catch (error) {
            console.error(
                "Error starting FOTA queue:",
                error
            );

            showMessage(
                error?.response?.data?.detail ||
                    error?.response?.data?.message ||
                    "Failed to start FOTA queue",
                "error"
            );
        } finally {
            setStartQueueLoading(false);
        }
    };


    /* =========================================================
       QUEUE READY ROW SELECTION
    ========================================================= */

    const isRowReady = (row) => {
        /*
         * Backend says mark-done validates:
         * fota_status == confirmed
         * connectivity_verified == truthy
         *
         * Stage should normally be ready.
         */
        return (
            String(row.stage).toLowerCase() === "ready" &&
            String(row.fota_status).toLowerCase() ===
                "confirmed" &&
            Boolean(row.connectivity_verified)
        );
    };


    const readyRows = useMemo(() => {
        return queueRows.filter(isRowReady);
    }, [queueRows]);


    const readyImeis = useMemo(() => {
        return readyRows
            .map((row) => String(row.imei))
            .filter(Boolean);
    }, [readyRows]);


    const allReadySelected =
        readyImeis.length > 0 &&
        readyImeis.every((imei) =>
            selectedReadyImeis.includes(imei)
        );


    const handleReadyRowSelection = (imei) => {
        const imeiString = String(imei);

        setSelectedReadyImeis((previous) => {
            if (previous.includes(imeiString)) {
                return previous.filter(
                    (item) => item !== imeiString
                );
            }

            return [...previous, imeiString];
        });
    };


    const handleSelectAllReady = (event) => {
        if (event.target.checked) {
            setSelectedReadyImeis(readyImeis);
        } else {
            setSelectedReadyImeis([]);
        }
    };


    /* =========================================================
       MARK CONFIG DONE
    ========================================================= */

    const handleMarkConfigDone = async () => {
        if (selectedReadyImeis.length === 0) {
            showMessage(
                "Please select at least one ready device.",
                "warning"
            );
            return;
        }

        try {
            setMarkDoneLoading(true);

            const response =
                await FactoryStockService.fotaQueueMarkDone(
                    {
                        imeis: selectedReadyImeis,
                    }
                );

            const results =
                response?.data?.results || [];

            const successCount =
                results.filter(
                    (item) =>
                        item.status === "success"
                ).length;

            const errorCount =
                results.filter(
                    (item) =>
                        item.status === "error"
                ).length;

            if (errorCount > 0) {
                showMessage(
                    `${successCount} marked done, ${errorCount} failed.`,
                    "warning"
                );
            } else {
                showMessage(
                    `${successCount} device(s) marked config done.`,
                    "success"
                );
            }

            setSelectedReadyImeis([]);

            await loadQueueStatus();
            await loadTodoDevices();
        } catch (error) {
            console.error(
                "Error marking config done:",
                error
            );

            showMessage(
                error?.response?.data?.detail ||
                    error?.response?.data?.message ||
                    "Failed to mark configuration done",
                "error"
            );
        } finally {
            setMarkDoneLoading(false);
        }
    };


    /* =========================================================
       RETRY
    ========================================================= */

    const handleRetry = async (imei) => {
        const imeiString = String(imei);

        try {
            setRetryLoading(imeiString);

            const response =
                await FactoryStockService.fotaQueueRetry(
                    {
                        imeis: [imeiString],
                    }
                );

            const results =
                response?.data?.results || [];

            const result = results[0];

            if (result?.status === "success") {
                showMessage(
                    `${imeiString} requeued successfully.`,
                    "success"
                );
            } else {
                showMessage(
                    result?.error ||
                        "Retry failed.",
                    "error"
                );
            }

            await loadQueueStatus();
        } catch (error) {
            console.error(
                "Error retrying device:",
                error
            );

            showMessage(
                error?.response?.data?.detail ||
                    error?.response?.data?.message ||
                    "Failed to retry device",
                "error"
            );
        } finally {
            setRetryLoading("");
        }
    };


    /* =========================================================
       HELPERS
    ========================================================= */

    const formatDate = (value) => {
        if (!value) {
            return "-";
        }

        try {
            return new Date(value).toLocaleString();
        } catch {
            return value;
        }
    };


    const getStageChip = (stage) => {
        const value =
            String(stage || "").toLowerCase();

        let color = "default";

        if (value === "ready") {
            color = "success";
        } else if (
            value === "queued" ||
            value === "awaiting_fota" ||
            value === "awaiting_connectivity"
        ) {
            color = "default";
        } else if (
            value.includes("failed") ||
            value === "error"
        ) {
            color = "error";
        }

        return (
            <Chip
                label={stage || "-"}
                size="small"
                color={color}
                sx={{
                    fontWeight: 500,
                }}
            />
        );
    };


    /* =========================================================
       UI
    ========================================================= */

    return (
        <Box
            sx={{
                p: 2,
                backgroundColor: "#fff",
                minHeight: "100vh",
            }}
        >

            {/* =================================================
                SECTION 1
            ================================================= */}

            <Typography
                variant="h6"
                sx={{
                    color: "#007c7c",
                    fontWeight: 700,
                    mb: 1.5,
                }}
            >
                1. To-do list (devices not yet config-done)
            </Typography>


           <Stack
    direction={{
        xs: "column",
        sm: "row",
    }}
    spacing={1.5}
    alignItems={{
        xs: "stretch",
        sm: "center",
    }}
    sx={{ mb: 1.5 }}
>
    {/* Search Input */}
    <TextField
        size="small"
        placeholder="search uid/imei/iccid..."
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        onKeyDown={(e) => {
            if (e.key === "Enter") {
                handleSearch();
            }
        }}
        sx={{
            width: {
                xs: "100%",
                sm: 325,
            },
        }}
        InputProps={{
            startAdornment: (
                <SearchIcon
                    sx={{
                        mr: 1,
                        color: "text.secondary",
                    }}
                />
            ),
        }}
    />

    {/* Search Button */}
    <Button
        variant="contained"
        onClick={handleSearch}
        disabled={todoLoading}
        startIcon={
            todoLoading ? (
                <CircularProgress
                    size={18}
                    color="inherit"
                />
            ) : (
                <SearchIcon />
            )
        }
        sx={{
            minHeight: 40,
            textTransform: "none",
            fontSize: 16,
            borderRadius: 2,
            backgroundColor: "#0ca7a7",
            "&:hover": {
                backgroundColor: "#078f8f",
            },
        }}
    >
        Search
    </Button>

    {/* Refresh Button */}
    <Button
        variant="outlined"
        onClick={handleRefresh}
        disabled={todoLoading}
        startIcon={
            todoLoading ? (
                <CircularProgress size={18} />
            ) : (
                <RefreshIcon />
            )
        }
        sx={{
            minHeight: 40,
            textTransform: "none",
            fontSize: 16,
            borderRadius: 2,
            color: "#007c7c",
            borderColor: "#00a5a5",
            "&:hover": {
                borderColor: "#007c7c",
                backgroundColor: "#f0ffff",
            },
        }}
    >
        Refresh
    </Button>

    {/* Selected Count */}
    <Typography
        sx={{
            fontSize: 16,
            whiteSpace: "nowrap",
        }}
    >
        Selected:{" "}
        <strong>{selectedImeis.length}</strong>
        {" / "}
        {todoDevices.length} shown
    </Typography>
</Stack>


            <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                    mb: 3,
                    boxShadow: "none",
                }}
            >
                <Table
                    size="small"
                    sx={{
                        minWidth: 1000,
                    }}
                >
                    <TableHead>
                        <TableRow
                            sx={{
                                "& th": {
                                    fontWeight: 700,
                                    whiteSpace: "nowrap",
                                    backgroundColor: "#fafafa",
                                },
                            }}
                        >
                            <TableCell padding="checkbox">
                                <Checkbox
                                    checked={
                                        allTodoSelected
                                    }
                                    indeterminate={
                                        selectedImeis.length >
                                            0 &&
                                        !allTodoSelected
                                    }
                                    onChange={
                                        handleSelectAllTodo
                                    }
                                />
                            </TableCell>

                            <TableCell>
                                IMEI
                            </TableCell>

                            <TableCell>
                                Vendor
                            </TableCell>

                            <TableCell>
                                Model
                            </TableCell>

                            <TableCell>
                                Firmware
                            </TableCell>

                            <TableCell>
                                FOTA Status
                            </TableCell>

                            <TableCell>
                                Connectivity
                            </TableCell>

                            <TableCell>
                                Last Seen
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {todoLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={8}
                                    align="center"
                                    sx={{ py: 4 }}
                                >
                                    <CircularProgress
                                        size={28}
                                    />
                                </TableCell>
                            </TableRow>
                        ) : todoDevices.length ===
                          0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={8}
                                    align="center"
                                    sx={{ py: 4 }}
                                >
                                    No devices found
                                </TableCell>
                            </TableRow>
                        ) : (
                            todoDevices.map(
                                (device) => {
                                    const imei =
                                        String(
                                            device.imei
                                        );

                                    const checked =
                                        selectedImeis.includes(
                                            imei
                                        );

                                    return (
                                        <TableRow
                                            key={
                                                device.id ||
                                                imei
                                            }
                                            hover
                                            selected={
                                                checked
                                            }
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={
                                                        checked
                                                    }
                                                    onChange={() =>
                                                        handleTodoRowSelection(
                                                            imei
                                                        )
                                                    }
                                                />
                                            </TableCell>

                                            <TableCell>
                                                {device.imei ||
                                                    "-"}
                                            </TableCell>

                                            <TableCell>
                                                {device.fota_vendor ||
                                                    device.vendor ||
                                                    "-"}
                                            </TableCell>

                                            <TableCell>
                                                {device.device_model_name ||
                                                    "-"}
                                            </TableCell>

                                            <TableCell>
                                                {device.fota_protocol_format ||
                                                    device.fota_version ||
                                                    "-"}
                                            </TableCell>

                                            <TableCell>
                                                {device.fota_status ||
                                                    "-"}
                                            </TableCell>

                                            <TableCell>
                                                {device.connectivity_verified
                                                    ? "yes"
                                                    : "no"}
                                            </TableCell>

                                            <TableCell
                                                sx={{
                                                    whiteSpace:
                                                        "nowrap",
                                                }}
                                            >
                                                {formatDate(
                                                    device.last_seen_at
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                }
                            )
                        )}
                    </TableBody>
                </Table>
            </TableContainer>


            {/* =================================================
                SECTION 2
            ================================================= */}

            <Typography
                variant="h6"
                sx={{
                    color: "#007c7c",
                    fontWeight: 700,
                    mb: 1.5,
                }}
            >
                2. Manufacturer / model / firmware
                (applies to the whole batch)
            </Typography>


           <Stack
    direction="row"
    flexWrap="wrap"
    spacing={1.5}
    useFlexGap
    sx={{
        mb: 3,
        width: "100%",
    }}
>
    {/* Vendor */}
    <FormControl
        size="small"
        sx={{
            width: {
                xs: "100%",
                sm: "calc(50% - 8px)",
                lg: "calc(25% - 12px)",
            },
            minWidth: 0,
        }}
    >
        <InputLabel>Vendor</InputLabel>

        <Select
            value={vendor}
            label="Vendor"
            onChange={handleVendorChange}
            fullWidth
        >
            <MenuItem value="">
                -- select vendor --
            </MenuItem>

            <MenuItem value="MAPW">
                MAPW
            </MenuItem>
        </Select>
    </FormControl>


    {/* Manufacturer */}
    <FormControl
        size="small"
        disabled={!vendor || catalogLoading}
        sx={{
            width: {
                xs: "100%",
                sm: "calc(50% - 8px)",
                lg: "calc(25% - 12px)",
            },
            minWidth: 0,
        }}
    >
        <InputLabel>Manufacturer</InputLabel>

        <Select
            value={selectedManufacturer}
            label="Manufacturer"
            onChange={(e) => {
                setSelectedManufacturer(e.target.value);
                setSelectedModel("");
                setSelectedFirmware("");
            }}
            fullWidth
        >
            <MenuItem value="">
                -- select manufacturer --
            </MenuItem>

            {manufacturerOptions.map((manufacturer) => (
                <MenuItem
                    key={manufacturer.id}
                    value={manufacturer.id}
                >
                    {manufacturer.company_name}
                </MenuItem>
            ))}
        </Select>
    </FormControl>


    {/* Model */}
    <FormControl
        size="small"
        disabled={!selectedManufacturer}
        sx={{
            width: {
                xs: "100%",
                sm: "calc(50% - 8px)",
                lg: "calc(25% - 12px)",
            },
            minWidth: 0,
        }}
    >
        <InputLabel>Model</InputLabel>

        <Select
            value={selectedModel}
            label="Model"
            onChange={(e) => {
                setSelectedModel(e.target.value);
                setSelectedFirmware("");
            }}
            fullWidth
        >
            <MenuItem value="">
                -- select model --
            </MenuItem>

            {modelOptions.map((model) => (
                <MenuItem
                    key={model.id}
                    value={model.id}
                >
                    {model.model_name}
                </MenuItem>
            ))}
        </Select>
    </FormControl>


    {/* Firmware */}
    <FormControl
        size="small"
        disabled={!selectedModel}
        sx={{
            width: {
                xs: "100%",
                sm: "calc(50% - 8px)",
                lg: "calc(25% - 12px)",
            },
            minWidth: 0,
        }}
    >
        <InputLabel>Firmware</InputLabel>

        <Select
            value={selectedFirmware}
            label="Firmware"
            onChange={(e) =>
                setSelectedFirmware(e.target.value)
            }
            fullWidth
        >
            <MenuItem value="">
                -- select firmware --
            </MenuItem>

            {firmwareOptions.map((firmware) => (
                <MenuItem
                    key={firmware.id}
                    value={firmware.id}
                >
                    {firmware.fw_name}
                    {firmware.version
                        ? ` (${firmware.version})`
                        : ""}
                </MenuItem>
            ))}
        </Select>
    </FormControl>
</Stack>


            {/* =================================================
                SECTION 3
            ================================================= */}

            <Typography
                variant="h6"
                sx={{
                    color: "#007c7c",
                    fontWeight: 700,
                    mb: 1.5,
                }}
            >
                3. Start queue
            </Typography>


            <Button
                variant="contained"
                startIcon={
                    startQueueLoading ? (
                        <CircularProgress
                            size={20}
                            color="inherit"
                        />
                    ) : (
                        <PlayArrowIcon />
                    )
                }
                disabled={
                    startQueueLoading ||
                    selectedImeis.length === 0 ||
                    !vendor ||
                    !selectedManufacturer ||
                    !selectedModel ||
                    !selectedFirmware
                }
                onClick={
                    handleStartQueue
                }
                sx={{
                    mb: 3,
                    backgroundColor: "#0ca7a7",
                    textTransform: "none",
                    fontSize: 17,
                    borderRadius: 2,
                    "&:hover": {
                        backgroundColor:
                            "#078f8f",
                    },
                }}
            >
                Start Queue with Selected
                Devices
            </Button>


            {/* =================================================
                SECTION 4
            ================================================= */}

            <Typography
                variant="h6"
                sx={{
                    color: "#007c7c",
                    fontWeight: 700,
                    mb: 1.5,
                }}
            >
                4. Queue status
            </Typography>


            <Stack
                direction={{
                    xs: "column",
                    sm: "row",
                }}
                spacing={1.5}
                sx={{ mb: 1.5 }}
            >
                <Button
                    variant="outlined"
                    startIcon={
                        queueLoading ? (
                            <CircularProgress
                                size={18}
                            />
                        ) : (
                            <RefreshIcon />
                        )
                    }
                    disabled={queueLoading}
                    onClick={
                        loadQueueStatus
                    }
                    sx={{
                        textTransform: "none",
                        fontSize: 16,
                        color: "#007c7c",
                        borderColor: "#00a5a5",
                    }}
                >
                    Refresh now
                </Button>


                <Button
                    variant="contained"
                    startIcon={
                        markDoneLoading ? (
                            <CircularProgress
                                size={20}
                                color="inherit"
                            />
                        ) : (
                            <CheckCircleIcon />
                        )
                    }
                    disabled={
                        markDoneLoading ||
                        selectedReadyImeis.length ===
                            0
                    }
                    onClick={
                        handleMarkConfigDone
                    }
                    sx={{
                        backgroundColor:
                            "#0ca7a7",
                        textTransform: "none",
                        fontSize: 16,
                        "&:hover": {
                            backgroundColor:
                                "#078f8f",
                        },
                    }}
                >
                    Mark Config Done
                    {" (checked 'ready' rows)"}
                </Button>

                <Typography
                    sx={{
                        alignSelf: "center",
                        color: "text.secondary",
                    }}
                >
                    Selected ready:{" "}
                    <strong>
                        {
                            selectedReadyImeis.length
                        }
                    </strong>
                </Typography>
            </Stack>


            <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                    boxShadow: "none",
                    overflowX: "auto",
                }}
            >
                <Table
                    size="small"
                    sx={{
                        minWidth: 1250,
                    }}
                >
                    <TableHead>
                        <TableRow
                            sx={{
                                "& th": {
                                    fontWeight: 700,
                                    whiteSpace:
                                        "nowrap",
                                    backgroundColor:
                                        "#fafafa",
                                },
                            }}
                        >
                            <TableCell>
                                IMEI
                            </TableCell>

                            <TableCell>
                                Vendor
                            </TableCell>

                            <TableCell>
                                Model
                            </TableCell>

                            <TableCell>
                                Firmware
                            </TableCell>

                            <TableCell>
                                Stage
                            </TableCell>

                            <TableCell>
                                FOTA
                            </TableCell>

                            <TableCell>
                                Connectivity
                            </TableCell>

                            <TableCell>
                                Last Error
                            </TableCell>

                            <TableCell>
                                Last Updated
                            </TableCell>

                            <TableCell
                                padding="checkbox"
                                align="center"
                            >
                                Actions
                            </TableCell>
                        </TableRow>
                    </TableHead>


                    <TableBody>
                        {queueLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={10}
                                    align="center"
                                    sx={{
                                        py: 4,
                                    }}
                                >
                                    <CircularProgress
                                        size={28}
                                    />
                                </TableCell>
                            </TableRow>
                        ) : queueRows.length ===
                          0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={10}
                                    align="center"
                                    sx={{
                                        py: 4,
                                    }}
                                >
                                    No queue devices
                                    found
                                </TableCell>
                            </TableRow>
                        ) : (
                            queueRows.map(
                                (row) => {
                                    const imei =
                                        String(
                                            row.imei
                                        );

                                    const ready =
                                        isRowReady(
                                            row
                                        );

                                    const checked =
                                        selectedReadyImeis.includes(
                                            imei
                                        );

                                    const stage =
                                        String(
                                            row.stage ||
                                                ""
                                        ).toLowerCase();

                                    const retryAllowed =
                                        stage ===
                                            "fota_failed" ||
                                        stage ===
                                            "connectivity_failed" ||
                                        stage ===
                                            "error";

                                    return (
                                        <TableRow
                                            key={
                                                row.id ||
                                                `${row.batch_id}-${imei}`
                                            }
                                            hover
                                        >
                                            <TableCell>
                                                {row.imei ||
                                                    "-"}
                                            </TableCell>

                                            <TableCell>
                                                {row.vendor ||
                                                    "-"}
                                            </TableCell>

                                            <TableCell>
                                                {row.device_model_name ||
                                                    "-"}
                                            </TableCell>

                                            <TableCell>
                                                {row.protocol_format ||
                                                    "-"}
                                            </TableCell>

                                            <TableCell>
                                                {getStageChip(
                                                    row.stage
                                                )}
                                            </TableCell>

                                            <TableCell>
                                                <Stack
                                                    direction="row"
                                                    spacing={0.5}
                                                    alignItems="center"
                                                >
                                                    <span>
                                                        {
                                                            row.fota_status
                                                        }
                                                    </span>

                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        (
                                                        resend{" "}
                                                        {
                                                            row.fota_resend_count ??
                                                                0
                                                        }
                                                        )
                                                    </Typography>
                                                </Stack>
                                            </TableCell>

                                            <TableCell>
                                                <Stack
                                                    direction="row"
                                                    spacing={0.5}
                                                    alignItems="center"
                                                >
                                                    <span>
                                                        {row.connectivity_verified
                                                            ? "yes"
                                                            : "no"}
                                                    </span>

                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        (
                                                        resend{" "}
                                                        {
                                                            row.connectivity_resend_count ??
                                                                0
                                                        }
                                                        )
                                                    </Typography>
                                                </Stack>
                                            </TableCell>

                                            <TableCell
                                                sx={{
                                                    maxWidth: 250,
                                                    color: row.last_error
                                                        ? "error.main"
                                                        : "text.primary",
                                                }}
                                            >
                                                {row.last_error ||
                                                    "-"}
                                            </TableCell>

                                            <TableCell
                                                sx={{
                                                    whiteSpace:
                                                        "nowrap",
                                                }}
                                            >
                                                {formatDate(
                                                    row.updated_at
                                                )}
                                            </TableCell>

                                            <TableCell
                                                padding="checkbox"
                                                align="center"
                                            >
                                                {ready ? (
                                                    <Checkbox
                                                        checked={
                                                            checked
                                                        }
                                                        onChange={() =>
                                                            handleReadyRowSelection(
                                                                imei
                                                            )
                                                        }
                                                        title="Select ready row"
                                                    />
                                                ) : retryAllowed ? (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="warning"
                                                        startIcon={
                                                            retryLoading ===
                                                            imei ? (
                                                                <CircularProgress
                                                                    size={
                                                                        16
                                                                    }
                                                                />
                                                            ) : (
                                                                <ReplayIcon />
                                                            )
                                                        }
                                                        disabled={
                                                            retryLoading ===
                                                            imei
                                                        }
                                                        onClick={() =>
                                                            handleRetry(
                                                                imei
                                                            )
                                                        }
                                                        sx={{
                                                            textTransform:
                                                                "none",
                                                        }}
                                                    >
                                                        Retry
                                                    </Button>
                                                ) : (
                                                    "-"
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                }
                            )
                        )}
                    </TableBody>
                </Table>
            </TableContainer>


            {/* =================================================
                SNACKBAR
            ================================================= */}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={
                    handleSnackbarClose
                }
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
            >
                <Alert
                    severity={
                        snackbar.severity
                    }
                    onClose={
                        handleSnackbarClose
                    }
                    variant="filled"
                    sx={{
                        width: "100%",
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

        </Box>
    );
};

export default BulkFotaQueue;